import { useDndContext } from "@dnd-kit/core";
import { Badge, Box, Card, useTheme } from "@mui/material";
import React from "react";

import { RobotoTypography } from "@/shared/components";
import { FileSystemNode } from "@/shared/domain/files/filesystem";
import { FileSystemNodeType } from "@/shared/domain/files/filesystem/fileSystemNode";

import { MultiCardStack } from "./MultiCardStack";
import { RowTypeIcon } from "./RowTypeIcon";

interface FileDragOverlayProps {
  activeDraggable: FileSystemNode | null;
  itemCount: number;
}

export interface NudgeRef {
  nudgeX: number;
  nudgeY: number;
}

export const FileDragOverlay: React.FC<FileDragOverlayProps> = ({
  activeDraggable,
  itemCount,
}) => {
  const theme = useTheme();
  const dndContext = useDndContext();

  const overlayRef = React.useRef<HTMLDivElement>(null);

  const nudgeRef = React.useRef<NudgeRef>({
    nudgeX: 0,
    nudgeY: 0,
  });

  React.useLayoutEffect(() => {
    // This component is mounted only during a drag.
    // We intentionally run this effect once to measure initial drag layout.
    // Calculations must be performed inside useLayoutEffect to ensure DOM is available
    const leftStartPosition = dndContext.activeNodeRect?.left ?? 0;
    const topStartPosition = dndContext.activeNodeRect?.top ?? 0;

    const elementWidth = overlayRef?.current?.clientWidth ?? 0;
    const elementHeight = overlayRef?.current?.clientHeight ?? 0;

    const activatorEvent = dndContext.activatorEvent;

    let eventStartX = 0;
    let eventStartY = 0;

    if (activatorEvent instanceof PointerEvent) {
      eventStartX = activatorEvent.clientX ?? 0;
      eventStartY = activatorEvent.clientY ?? 0;
    }

    // calculate the amount to "nudge" the overlay element to center it on the cursor
    const nudgeX = eventStartX - leftStartPosition - elementWidth / 2;
    const nudgeY = eventStartY - topStartPosition - elementHeight / 2;

    // Nudge values should be updated once and only once
    // Need to set the entire object here
    if (nudgeRef && typeof nudgeRef === "object") {
      nudgeRef.current = {
        nudgeX,
        nudgeY,
      };
    }
    // This effect should only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const left =
    typeof nudgeRef === "object" && nudgeRef?.current
      ? nudgeRef.current.nudgeX
      : 0;
  const top =
    typeof nudgeRef === "object" && nudgeRef?.current
      ? nudgeRef.current.nudgeY
      : 0;

  return (
    <Card
      sx={{
        backgroundColor: theme.palette.paper.main,
        padding: 0,
        borderRadius: theme.border.radius,
        border: `1px solid ${theme.palette.primary.dark}`,
        position: "relative",
        left,
        top,
        width: "300px",
        height: "2.3rem",
        cursor: "grabbing",
        overflow: "visible",
      }}
      ref={overlayRef}
      data-cy="dnd-overlay"
      raised={true}
    >
      <MultiCardStack stackEnabled={itemCount > 1}>
        <Badge
          badgeContent={itemCount}
          color="primary"
          sx={{ width: "100%", height: "100%" }}
          max={9}
          invisible={itemCount <= 1}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              minWidth: "200px",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <RowTypeIcon
              isRowLoading={false}
              rowName={activeDraggable?.name ?? ""}
              rowType={activeDraggable?.type ?? FileSystemNodeType.File}
            />
            <RobotoTypography
              sx={{
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                display: "block",
                marginLeft: theme.spacing(1),
                maxWidth: "240px",
              }}
              variant={"body2"}
            >
              {activeDraggable?.name}
            </RobotoTypography>
          </Box>
        </Badge>
      </MultiCardStack>
    </Card>
  );
};
